import type { Vocabulary } from './vocabulary.types'

/**
 * The NEUTRAL BASE — vertical-agnostic operator copy. Every user-facing string lives here (or in a
 * per-screen section here); a vertical overrides only what it says differently ({@link
 * vocabulary.logistics.ts}). label / sender / device are platform terms and stay neutral unless a
 * vertical renames them.
 */
export const base: Vocabulary = {
  terms: {
    adminDevice: 'Admin device',
    adminDevices: 'Admin devices',
    readerDevice: 'Reader device',
    readerDevices: 'Reader devices',
    readers: 'Readers',
    sender: 'Sender',
    senders: 'Senders',
  },

  glossary: {
    published: {
      title: 'Labels published',
      body: 'Labels your senders have sent. Only the organizations you sealed them to can read what is inside — we hold them and cannot open them.',
    },
    received: {
      title: 'Labels received',
      body: 'Labels other organizations sealed to you. Your reader devices can open these; this counts what has arrived, not what has been opened.',
    },
    admin: {
      title: 'Admin device',
      body: 'A phone that approves reader devices for your organization. Approving happens on the phone itself, so nobody here — including us — can let a device in.',
    },
    reader: {
      title: 'Reader device',
      body: 'A handheld or phone that scans labels and reads what is inside. It works once an admin device approves it, and stops the moment you revoke it.',
    },
    server: {
      title: 'Sender',
      body: 'One of your systems that publishes labels. It creates its own credentials the first time it starts, so there is no password for you to store or rotate.',
    },
    risk: {
      title: 'Risk',
      body: 'A running score of how unusually this device is being used — higher means more concern: wrong verification codes, reads far outside authorized areas, screenshot attempts, sudden re-enrollments, or a pace and shift unlike its own history. It looks at how the device behaves, never at what the labels say, and a high score never blocks anything by itself.',
    },
  },

  riskRules: {
    'wrong-otp-burst': 'Wrong verification codes',
    'out-of-area': 'Reads outside the authorized area',
    'screenshot-on-reveal': 'Screenshot attempts',
    'impossible-travel': 'Impossible travel between reads',
    'enrollment-velocity': 'Repeated device enrollments',
    'reveal-harvest': 'Combing through label details',
    'call-frequency': 'Repeated calls to one contact',
    'read-surge': 'Sudden jump in their own pace',
    'odd-hours': 'Reads at unfamiliar hours',
  },

  permissions: [
    { slug: 'delegator-admin', label: 'Set up devices', hint: 'pair admin devices, invite reader devices' },
    { slug: 'developer', label: 'Connect senders', hint: 'add the systems that publish labels' },
    { slug: 'owner-admin', label: 'Manage the team', hint: 'invite people, change what they can do, remove them' },
  ],

  nav: [
    { to: '/home', label: 'Home' },
    { to: '/devices', label: 'Devices' },
    { to: '/servers', label: 'Senders' },
    { to: '/team', label: 'Team' },
    { to: '/labels', label: 'Labels' },
  ],

  common: {
    cancel: 'Cancel',
    confirm: 'Confirm',
    working: 'Working…',
    save: 'Save',
    copy: 'Copy',
    copyLink: 'Copy link',
    copied: 'Copied',
    close: 'Close',
    loading: 'Loading',
    previous: 'Previous',
    next: 'Next',
    pageWord: 'Page',
    ofWord: 'of',
    downloadQr: 'Download QR',
    qrAlt: 'Scan this code with the app on the device',
    whatIsAPrefix: 'What is a ',
    whatIsASuffix: '?',
    countdownPrefix: 'Expires in',
    countdownExpired: 'Expired',
    timeExpired: 'expired',
    inPrefix: 'in',
    seconds: 'seconds',
    minutes: 'minutes',
    hours: 'hours',
    days: 'days',
  },

  shell: {
    navAria: 'Sections',
    accountAria: 'Account',
    brandAlt: 'āvṛtam',
    aboutOslp: 'About — built on OSLP',
    signOut: 'Sign out',
    joinedPrefix: 'You joined ',
    joinedSuffix: '.',
    theOrganization: 'the organization',
    joinFailed: 'That invite could not be used.',
    joining: 'Joining…',
    goToOrg: 'Go to your organization',
    continueWord: 'Continue',
  },

  signIn: {
    promise: 'Only the people you choose can read what you send.',
    pitch: 'Send from your systems. Approve the devices that read. We hold what you send and cannot open it.',
    title: 'Sign in',
    invitedSub: 'You’ve been invited — sign in to join your team.',
    continueSub: 'Continue to your organization.',
    continueGoogle: 'Continue with Google',
    invitedNote: 'Signing in joins you to the organization that invited you.',
    newNote: 'New here? Signing in creates your account, then you name your organization.',
    builtOn: 'Built on',
    oslp: 'OSLP',
    protocolSuffix: ', an open protocol',
  },

  home: {
    subhead: 'What is happening in your organization.',
    active: 'active',
    shredded: 'shredded',
    attentionTitle: 'Needs your attention',
    atHighRisk: 'at high risk',
    waitingChip: 'waiting',
    highRiskFooter: 'Risk scores are advisory — they never block a courier. Open the device to see why it is flagged.',
    waitingFooter: 'Approvals happen on the admin device itself — open the app to approve.',
    highRiskOne: 'A reader device is flagged at high risk.',
    highRiskMany: 'reader devices are flagged at high risk.',
    reviewDevices: 'Review devices',
    waitingOne: 'A reader device is waiting for approval.',
    waitingMany: 'reader devices are waiting for approval.',
    viewDevices: 'View devices',
    getStarted: 'Get started',
    doEither: 'Do either — or both',
    sendLabels: 'Send labels',
    senderConnected: '✓ A sender is connected',
    connectPublisher: 'Connect the system that publishes your labels.',
    connectSender: 'Connect a sender',
    readLabels: 'Read labels',
    allSetUp: '✓ Admin and reader devices are set up',
    adminPairedInvite: '✓ Admin device paired — invite the devices that will read',
    pairThenInvite: 'Pair an admin device, then invite the devices that read.',
    inviteDevice: 'Invite a device',
    pairAdminDevice: 'Pair an admin device',
    notSureWhich: 'Not sure which you are? Most organizations do one or the other: you send labels, or you read them. Some do both.',
    loadError: 'Could not load this page.',
  },

  devices: {
    title: 'Devices',
    subhead: 'The phones and handhelds that read your labels, and the ones that approve them.',
    deviceGroupsAria: 'Device groups',
    bandHigh: 'High',
    bandWatch: 'Watch',
    bandClear: 'Clear',
    adminsTab: 'Admins',
    inviteDevice: 'Invite a device',
    copyInvite: 'Copy invite',
    readersFooter: 'A device works once an admin device approves it — and stops the moment you revoke it.',
    noReaders: 'No reader devices yet. Invite the first device that will read your labels.',
    askSetup: 'Ask someone who can set up devices.',
    colDevice: 'Device',
    colJoined: 'Joined',
    colStatus: 'Status',
    colRisk: 'Risk',
    unnamedDevice: 'Unnamed device',
    statusActive: 'Active',
    statusRevoked: 'Revoked',
    statusWaiting: 'Waiting for approval',
    riskAriaPrefix: 'Risk for',
    pairDevice: 'Pair a device',
    adminSecondFooter: 'Pair a second admin device, so a lost phone never locks you out.',
    noAdmins: 'No admin devices yet. Pair one to approve the devices that read your labels.',
    colPaired: 'Paired',
    statusReplaced: 'Replaced',
    getReaderApp: 'Get the reader app',
    appOptinFooter: 'This code never changes — new phones scan the same one, and installed phones update on their own.',
    readerAppInstall: 'Scan with the phone to join the test programme on Google Play and install the reader app — then come back here and invite it.',
    getAdminApp: 'Get the admin app',
    adminAppInstall: 'Scan with the phone to join the test programme on Google Play and install the admin app — then come back here and pair it.',
    pairAdminTitle: 'Pair an admin device',
    pairAdminFooter: 'An admin device approves reader devices and can be used from anywhere.',
    pairStep1: 'Install the admin app on the phone.',
    pairStep2: 'Open it and scan this code.',
    pairStep3: "Confirm the phone shows your organization's name.",
    codeExpiresIn: 'Code expires in',
    codeExpired: 'Code expired — get a new one',
    newCode: 'New code',
    inviteReaderTitle: 'Invite a reader device',
    inviteReaderFooter: 'It appears in your list, waiting for approval on an admin device.',
    labelThisDevice: 'Label this device (optional)',
    deviceNamePlaceholder: 'Dock 2 scanner — so you can tell them apart',
    rename: 'Rename',
    shareInvite: 'Share it any way you reach them.',
    expiresWord: 'Expires',
    worksOnce: '· works once.',
    loadError: 'Could not load your devices.',
    pairError: 'Could not create a pairing code.',
    inviteError: 'Could not create an invite.',
    backToDevices: '← Devices',
    riskTitleSuffix: '· risk',
    computedNow: 'Computed just now',
    refreshing: 'Refreshing…',
    last14: '· last 14 days',
    noActivity: 'No activity from this device in the current window — nothing to score.',
    riskOutOf: '/ 100',
    whyTitle: 'Why',
    whyFooter: 'Every point accounted for. Factors fade as their window rolls.',
    noFactors: 'No factors — behaving like themselves.',
    summaryTitle: 'Summary',
    generated: 'Generated',
    writtenFrom: 'Written from the factors above — nothing else',
    working: 'Working…',
    regenerate: 'Regenerate',
    highNoBlock: "High doesn't block anything by itself. Approval and revocation stay on the admin device.",
  },

  servers: {
    subhead: 'Your systems that publish labels.',
    loadError: 'Could not load your senders.',
    codeError: 'Could not create a setup code.',
    connected: 'Connected',
    connectSender: 'Connect a sender',
    noSenders: 'No senders yet. Connect the system that publishes your labels.',
    askOwner: 'Ask an owner to connect a sender.',
    colEnvironment: 'Environment',
    colConnected: 'Connected',
    colBy: 'By',
    unnamedSender: 'Unnamed sender',
    envProduction: 'Production',
    envTest: 'Test',
    settingsFile: 'Settings file',
    settingsFileDesc: "Put this on your application's classpath and it knows where to reach us — no endpoints in your code.",
    downloadSettings: 'Download settings file',
    connectModalTitle: 'Connect a sender',
    connectModalFooter: 'The sender creates its own credentials the first time it starts. Nothing else to copy, and the setup code stops working once it is used.',
    environment: 'Environment',
    testProdNote: 'Test and production are separate identities — a test sender can never publish as production.',
    generateCode: 'Generate setup code',
    setupCodeLabel: 'Setup code — copy it now, it is shown once',
    copy: 'Copy',
    expiresWord: 'Expires',
    thenOnSender: 'Then, on the sender',
    downloadFailed: 'Download failed.',
    downloadRetry: 'That file could not be downloaded. Try again.',
    propertiesFile: 'hosted-operator.properties',
  },

  team: {
    title: 'Team',
    subhead: 'People who can sign in to this organization.',
    members: 'Members',
    inviteTeammate: 'Invite teammate',
    colPerson: 'Person',
    colCan: 'Can',
    colJoined: 'Joined',
    you: '· you',
    change: 'Change',
    remove: 'Remove',
    editWhatPrefix: 'What',
    editWhatSuffix: 'can do',
    removeTitle: 'Remove from organization',
    removeConfirm: 'Remove',
    removeBody: ' will lose access to this organization and can no longer sign in to it. You can invite them again later.',
    inviteTitle: 'Invite a teammate',
    inviteFooter: 'The link works once, and expires in 7 days.',
    whatCanDo: 'What should they be able to do?',
    createInviteLink: 'Create invite link',
    sendLink: 'Send them this link',
    expiresWord: 'Expires',
    inviteExpiresSuffix: '. They sign in with Google, and join automatically.',
    loadError: 'Could not load your team.',
    saveError: 'That change did not save.',
    removeError: 'That person was not removed.',
    inviteError: 'Could not create an invite.',
  },

  labels: {
    title: 'Labels',
    subhead: 'Find a label by its public reference to see its details and reprint it.',
    placeholder: 'Public reference, e.g. OSLP-3705ab7b57e62ecb',
    refAria: 'Label public reference',
    finding: 'Finding…',
    findLabel: 'Find label',
    idlePrompt: "Paste a label's public reference to find it. It's printed on the label, and in the record you were sent.",
    missing: 'No label with that reference. Check for a typo — references are matched exactly.',
    statusActive: 'Active',
    statusExpired: 'Expired',
    statusErased: 'Erased',
    cardTitle: 'Label',
    publicReference: 'Public reference',
    published: 'Published',
    expires: 'Expires',
    publishedBy: 'Published by',
    copyPrintLink: 'Copy print link',
    open: 'Open',
    printPreview: 'Print preview',
    erasedPreview: 'This label was erased — there is nothing left to print.',
    expiredPreview: 'This label has expired — the print link no longer resolves.',
    loadingPreviewAria: 'Loading preview',
    reprintError: 'That reprint could not be loaded.',
    openNewTab: 'Open in a new tab',
    pdfMobile: "This label is a PDF — open or download it to view and print. Phones can't preview a PDF inline.",
    downloadPdf: 'Download PDF',
    otherFilePrefix: 'This label is a ',
    otherFileSuffix: " file — it can't be shown here, but you can still print it.",
    download: 'Download',
    renderedLabelAria: 'Rendered label',
    lookupError: 'Could not look up that label.',
  },

  createOrg: {
    title: 'Set up your organization',
    desc: 'This name appears on the devices your team pairs, so use the name they would recognise.',
    nameLabel: 'Organization name',
    namePlaceholder: 'Acme Logistics',
    creating: 'Creating…',
    createOrg: 'Create organization',
    ownerNote: 'You will be the owner. You can invite the rest of your team next.',
    saveError: 'That did not save. Try again.',
  },
}
